import React, { forwardRef, useId, useState } from 'react';
import { ICONS } from '../../constants/icons';

/**
 * Input
 * ------------------------------------------------------------------
 * Generic labeled text input. Controlled component: pass `value` and
 * `onChange` like a native input. Supports helper text and an error
 * state with a message, both wired up for screen readers.
 *
 * When `type="password"`, renders a show/hide eye toggle (no clear ×).
 * Optional `helperTone` applies only to password fields (live rules hint).
 *
 * @param {string} label - visible field label
 * @param {string} helperText - small muted text under the field
 * @param {'error'|'success'} helperTone - password-only live helper color
 * @param {string} error - error message; when present, field is invalid
 */
const CustomInput = forwardRef(
  (
    {
      label,
      helperText,
      helperTone,
      error,
      id,
      required = false,
      className = '',
      textarea = false,
      type,
      ...rest
    },
    ref
  ) => {
    const autoId = useId();
    const inputId = id || autoId;
    const describedBy = error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined;
    const Element = textarea ? 'textarea' : 'input';
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = !textarea && type === 'password';
    const { onFocus: userOnFocus, onBlur: userOnBlur, ...inputProps } = rest;
    const handleFocus = (e) => {
      setIsFocused(true);
      if (userOnFocus) userOnFocus(e);
    };
    const handleBlur = (e) => {
      setIsFocused(false);
      if (userOnBlur) userOnBlur(e);
    };
    const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : type;
    const elementProps = textarea
      ? { ...inputProps, onFocus: handleFocus, onBlur: handleBlur }
      : { ...inputProps, type: resolvedType, onFocus: handleFocus, onBlur: handleBlur };
    const hasClear = !textarea && !isPassword && !!inputProps.value && !!inputProps.onChange && isFocused;

    // Live red/green helper is password-only; other fields keep original behavior.
    const liveTone = isPassword ? helperTone : undefined;
    const showAsError = Boolean(error) || liveTone === 'error';
    const showAsSuccess = isPassword && !error && liveTone === 'success';
    const showHelper = Boolean(helperText) && (isPassword || !error);
    const helperClassName = [
      'amok-field__message',
      liveTone === 'error' ? 'amok-field__message--error' : '',
      liveTone === 'success' ? 'amok-field__message--success' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={[
          'amok-field',
          showAsError ? 'amok-field--error' : '',
          showAsSuccess ? 'amok-field--success' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {label && (
          <label htmlFor={inputId} className="amok-field__label">
            {label}
            {required && <span className="amok-field__required"> *</span>}
          </label>
        )}
        <div className="amok-field__input-wrap">
          <Element
            id={inputId}
            ref={ref}
            className={
              textarea
                ? 'amok-field__textarea'
                : [
                    'amok-field__input',
                    hasClear ? 'amok-field__input--clearable' : '',
                    isPassword ? 'amok-field__input--password' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')
            }
            aria-invalid={!!error || liveTone === 'error'}
            aria-describedby={describedBy}
            {...elementProps}
          />
          {hasClear && (
            <button
              type="button"
              className="amok-field__clear"
              aria-label="Clear input"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!inputProps.onChange) return;
                // Formik handleChange reads target.name — value alone is not enough
                inputProps.onChange({
                  target: {
                    name: inputProps.name || '',
                    id: inputId,
                    type: resolvedType || 'text',
                    value: '',
                  },
                });
              }}
            >
              ×
            </button>
          )}
          {isPassword && (
            <button
              type="button"
              className="amok-field__toggle-password"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPassword((prev) => !prev)}
            >
              <img
                src={showPassword ? ICONS.eyeSlash : ICONS.eye}
                alt=""
                aria-hidden="true"
                width={16}
                height={16}
              />
            </button>
          )}
        </div>
        {error ? (
          <p id={`${inputId}-error`} className="amok-field__message amok-field__message--error">
            {error}
          </p>
        ) : null}
        {showHelper ? (
          <p
            id={`${inputId}-helper`}
            className={helperClassName}
            {...(isPassword && liveTone
              ? { role: 'status', 'aria-live': 'polite' }
              : {})}
          >
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

CustomInput.displayName = 'CustomInput';

export default CustomInput;
